class YTVideo extends HTMLElement {
  static observedAttributes = ["video-id", "aspect", "start", "title"];
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._player = null;
    this._ready = false;
  }
  connectedCallback() {
    this.render();
  }
  attributeChangedCallback() {
    this.render();
  }

  parseAspect(a) {
    const [w, h] = String(a || "16:9")
      .split(":")
      .map(Number);
    return { w: w > 0 ? w : 16, h: h > 0 ? h : 9 };
  }

  render() {
    const id = this.getAttribute("video-id");
    if (!id) {
      this.shadowRoot.innerHTML = "";
      return;
    }

    const { w, h } = this.parseAspect(this.getAttribute("aspect"));
    const title = this.getAttribute("title") || "YouTube video";
    const start = parseInt(this.getAttribute("start") || "0", 10) || 0;
    const uid = `ytp-${id}-${Math.random().toString(36).slice(2)}`;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          max-width: 100%;
        }
        .wrap {
          position: relative;
          width: 100%;
          aspect-ratio: ${w} / ${h};
          background: #000 center / cover no-repeat;
          overflow: hidden;
        }
        @supports not (aspect-ratio: 1 / 1) {
          .wrap {
            height: 0;
            padding-bottom: ${((h / w) * 100).toFixed(6)}%;
          }
        }
        .inner {
          position: absolute;
          inset: 0;
        }
        .player {
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        .wrap.playing .player {
          opacity: 1;
        }
        .overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: auto;
          background: transparent;
          border: 0;
          padding: 0;
          margin: 0;
          cursor: pointer;
        }
        .overlay:hover .video-play-button {
          transform: scale(1.05);
        }
        .wrap.playing .overlay {
          opacity: 0;
          pointer-events: none;
        }
        .video-play-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: linear-gradient(to right, rgba(0, 0, 0, 0.3), rgba(102, 102, 102, 0.3));
          color: #fff;
          border-radius: 9999px;
          padding: 1rem 2rem;
          z-index: 2;
          border: 1px solid rgba(255, 255, 255, 0.3);
          transition: transform 300ms;
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
        }
        .overlay:focus-visible .video-play-button {
          outline: 2px solid #fff;
          outline-offset: 2px;
        }
        .video-play-button .caption {
          text-transform: uppercase;
          font-size: 10px;
          line-height: 1.4;
          letter-spacing: 1px;
          font-family: 'Sharp Grotesk Book', sans-serif;
        }
        iframe {
          width: 100%;
          height: 100%;
          border: 0;
        }
      </style>

      <div class="wrap" part="wrapper">
        <div class="inner">
          <div id="${uid}" class="player" title="${title.replace(
      /"/g,
      "&quot;"
    )}"></div>
          <button class="overlay" part="overlay" aria-label="Play video">
            <div class="video-play-button">
              <span class="caption">Watch now</span>
              <svg width="21" height="24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M20.416 13.418 2.483 23.803a1.168 1.168 0 0 1-1.584-.44 1.599 1.599 0 0 1-.135-.575V2.019c0-.642.506-1.15 1.146-1.15.203 0 .405.068.573.17l17.899 10.384c.54.304.741 1.014.438 1.59-.101.168-.236.338-.438.44l.034-.035z" fill="#fff"/>
              </svg>
            </div>
          </button>
        </div>
      </div>
    `;

    const wrap = this.shadowRoot.querySelector(".wrap");
    const overlay = this.shadowRoot.querySelector(".overlay");
    const playerHost = this.shadowRoot.getElementById(uid);

    const setThumb = (url) => {
      wrap.style.backgroundImage = `url('${url}')`;
    };

    const tryMaxRes = () => {
      const maxres = `https://i.ytimg.com/vi/${encodeURIComponent(
        id
      )}/maxresdefault.jpg`;
      const probe = new Image();
      probe.onload = () => setThumb(maxres);
      probe.onerror = trySD;
      probe.src = maxres;
    };

    const trySD = () => {
      const sd = `https://i.ytimg.com/vi/${encodeURIComponent(
        id
      )}/sddefault.jpg`;
      const img = new Image();
      img.onload = () => setThumb(sd);
      img.onerror = () =>
        setThumb(
          `https://i.ytimg.com/vi/${encodeURIComponent(id)}/hqdefault.jpg`
        );
      img.src = sd;
    };

    tryMaxRes();

    // Leaf - Custom Event - start_video, complete_video - Start
    //
    // Gated on data-ga-video-title's presence rather than firing for every
    // <yt-video> instance - this component is also used on hero-v2.liquid
    // (homepage hero), which is out of scope and doesn't set this attribute.
    // Only sections/video-article.liquid sets it today. start_video only
    // fires once per instance (guarded by hasFiredStart) so resuming after a
    // pause/seek doesn't re-fire it - matches GA4's own video_start
    // convention of firing at the start of playback, not on every resume.
    const gaVideoTitle = this.getAttribute("data-ga-video-title");
    let hasFiredStart = false;
    // Leaf - Custom Event - start_video, complete_video - End

    this.loadYT().then((YT) => {
      this._player = new YT.Player(playerHost, {
        videoId: id,
        playerVars: {
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          controls: 1,
          origin: location.origin,
          start: start,
        },
        events: {
          onReady: () => {
            this._ready = true;
          },
          onStateChange: (e) => {
            // Leaf - Custom Event - start_video, complete_video - Start
            if (e.data === YT.PlayerState.PLAYING && gaVideoTitle && window.LeafCustomEvents && !hasFiredStart) {
              hasFiredStart = true;
              window.LeafCustomEvents.publish("start_video", { video_title: gaVideoTitle });
            }
            // Leaf - Custom Event - start_video, complete_video - End
            if (e.data === YT.PlayerState.ENDED) {
              wrap.classList.remove("playing");
              this._player.seekTo(0, true);
              this._player.stopVideo();

              // Leaf - Custom Event - start_video, complete_video - Start
              if (gaVideoTitle && window.LeafCustomEvents) {
                window.LeafCustomEvents.publish("complete_video", { video_title: gaVideoTitle });
              }
              // Leaf - Custom Event - start_video, complete_video - End
            }
          },
        },
      });
    });

    const startPlay = () => {
      if (!this._player) return;

      if (!wrap.classList.contains("playing")) {
        wrap.classList.add("playing");

        const duration =
          this.closest("div")?.querySelector(".js-video-duration");
        if (duration) duration.classList.add("hidden");
      }

      if (this._ready) {
        this._player.playVideo();
      } else {
        const iv = setInterval(() => {
          if (this._ready) {
            clearInterval(iv);
            this._player.playVideo();
          }
        }, 50);
      }
    };

    overlay.addEventListener("click", startPlay);
    overlay.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        startPlay();
      }
    });
  }

  loadYT() {
    if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
    if (window.__ytApiPromise) return window.__ytApiPromise;
    window.__ytApiPromise = new Promise((resolve) => {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        if (typeof prev === "function") prev();
        resolve(window.YT);
      };
    });
    return window.__ytApiPromise;
  }
}
if (!customElements.get("yt-video")) {
  customElements.define("yt-video", YTVideo);
}
